import { ViteSSG } from 'vite-ssg';
import { createPinia } from 'pinia';
import { defineAsyncComponent } from 'vue';
import { initializeApp } from "firebase/app";
import { getAuth, setPersistence, browserSessionPersistence, onAuthStateChanged } from 'firebase/auth';
import { createHead } from '@vueuse/head';

// Importa el componente raíz y las rutas
import AppVue from './App.vue';
// ¡Importante! Importa el array de rutas, no la instancia del router por defecto.
import { routes } from './router'; 

// Importa tus estilos y otros archivos de configuración
import './style.css';
import './setPassiveEvents';

// Importa PrimeVue y sus componentes/estilos
import PrimeVue from 'primevue/config';
import Carousel from 'primevue/carousel';
import Galleria from 'primevue/galleria';
import Aura from '@primevue/themes/aura';
import 'primeicons/primeicons.css';

// Componentes globales que se registran de forma síncrona
import NewSlide from '@/pages/components/newSlide.vue';

// --- Constantes globales y exportaciones ---
const Solicitud = 'https://humanitas.edu.mx/assets/sendAPI.php';
const Solicitud2 = 'https://humanitas.edu.mx/assets/sendAPI2.php';
const colegiaturaLicEjec = 3080;
const colegiaturaLicEsco = 5200;
const colegiaturaMtr = 4400;
const colegiaturaDoc = 0;
export { Solicitud, Solicitud2, colegiaturaLicEjec, colegiaturaLicEsco, colegiaturaMtr, colegiaturaDoc };

// --- Declaraciones de tipos globales para TypeScript ---
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    ttq?: any;
    TiktokAnalyticsObject?: any;
  }
}

// --- Inicializador principal de la aplicación con ViteSSG ---
export const createApp = ViteSSG(
  // 1. Componente raíz de la aplicación
  AppVue,
  // 2. Opciones de configuración (rutas para vue-router)
  {
    routes, // Tu array de rutas importado
    strict: false,
    // Tu lógica de scrollBehavior, ahora aquí
    scrollBehavior(_to, _from, savedPosition) {
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          if (savedPosition) {
            resolve(savedPosition);
          } else {
            resolve({ top: 0, left: 0 });
          }
        });
      });
    },
  },
  // 3. Callback para configurar la instancia de la aplicación
  ({ app, router, isClient }) => {
    
    // Configura Pinia para el manejo de estado
    const pinia = createPinia();
    app.use(pinia);
    const head = createHead();
    app.use(head);
    // Configura PrimeVue
    app.use(PrimeVue, {
      theme: { preset: Aura }
    });
    app.component('Carousel', Carousel);
    app.component('Galleria', Galleria);

    // Registra tus componentes globales
    
    app.component("NewSlide", NewSlide);
    app.component("VideoHiL", defineAsyncComponent(() => import('@/pages/components/VideoHiL.vue')));
    app.component("EgresadosHuma", defineAsyncComponent(() => import('@/pages/components/oferta/EgresadosHuma.vue')));
    app.component("InfoTresImagenes", defineAsyncComponent(() => import('@/pages/components/InfoTresImagenes.vue')));

    // Hook para actualizar el título de la página (no depende del cliente)
    // router.beforeEach((to, _from: any, next: () => void) => {
    //   const defaultTitle = 'Universidad Humanitas | 45 años de excelencia académica';
    //   if (typeof document !== 'undefined') {
    //     document.title = String(to.meta.title) || defaultTitle;
    //   }
    //   next();
    // });
    
    // --- CÓDIGO QUE SOLO SE EJECUTA EN EL NAVEGADOR (CLIENTE) ---
    if (isClient) {
      const firebaseConfig = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
        measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
      };
      
      const firebaseApp = initializeApp(firebaseConfig);
      const auth = getAuth(firebaseApp);
      
      setPersistence(auth, browserSessionPersistence)
        .catch((error) => {
          console.error('Error al establecer la persistencia de sesión:', error);
        });

      // Hooks del router para Google Analytics
      router.afterEach((to: { fullPath: any; }) => {
        window.gtag('config', 'G-WG56YHP3SR', {
          page_path: to.fullPath,
        });
      });
        
      // Guardián para limpiar URLs (movido desde router/index.ts)
    router.beforeEach((to, _, next) => {
        const forbiddenParams = ['__hstc=', '__hssc=', '__hsfp='];
        const hasForbiddenParams = forbiddenParams.some(param => to.query.hasOwnProperty(param));
        // const hasTrailingSlash = to.path !== '/' && to.path.endsWith('/');

        if (hasForbiddenParams) {
            next({ path: to.path, query: {} });
        }  else {
            next();
        }
    });

        // Guardián de autenticación de Firebase
        const ADMIN_UID = import.meta.env.VITE_ADMIN_UID;
        let isAuthChecked = false;
        router.beforeEach((to, _, next) => {
            const requiresAuth = to.matched.some(record => record.meta.requireAuth);
            const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin); // Detectamos si la ruta es exclusiva

            if (!requiresAuth) {
                return next();
            }

            // Función auxiliar para no repetir código dentro del onAuthStateChanged
            const validarAcceso = (user: any) => {
                // 1. Si la ruta pide Admin y el usuario NO es el Admin
                if (requiresAdmin && user.uid !== ADMIN_UID) {
                    // Lo mandamos a la vista de los mortales
                    return next('/50p0rT3/corr3os'); 
                }
                
                // 2. Si todo está bien, adelante
                return next();
            };

            if (isAuthChecked) {
                // --- YA ESTABA LOGUEADO ---
                const user = auth.currentUser;
                if (!user) {
                    next('/50p0rT3/login');
                } else {
                    validarAcceso(user);
                }
            } else {
                // --- PRIMERA CARGA (REFRESH) ---
                const unsubscribe = onAuthStateChanged(auth, user => {
                    isAuthChecked = true;
                    unsubscribe();
                    
                    if (user) {
                        validarAcceso(user);
                    } else {
                        next('/50p0rT3/login');
                    }
                });
            }
        });
    }
  }
);